import { useEffect } from 'react';
import BaseDialog from './BaseDialog';
import { DialogTitle } from '@headlessui/react';

// Reusable modal wrapper (header, scrollable body, footer) used across pages
// Props:
// - title: optional string title shown in header
// - onClose: callback to close the modal
// - children: modal body
// - footer: optional footer node (buttons, actions)
// - maxWidthClass: tailwind max-width class (default 'max-w-3xl')
export default function Modal({ title, onClose, children, footer, maxWidthClass = 'max-w-3xl', open = true }) {
  // Prevent body scroll and handle scrollbar to avoid layout shift
  useEffect(() => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;

      const header = document.querySelector('header');
      if (header) {
        header.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';

      const header = document.querySelector('header');
      if (header) {
        header.style.paddingRight = '';
      }
    };
  }, []);

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      showClose={false}
      maxWidthClass={maxWidthClass}
      panelClassName="flex flex-col max-h-[90vh]"
      zIndexClass="z-50"
    >
      {/* Header section with close button and optional title */}
      <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-200/70 dark:border-gray-600/70 relative">
        <button
          onClick={onClose}
          aria-label="Close modal"
          className="absolute top-3 right-3 text-2xl leading-none w-8 h-8 flex items-center justify-center border-none bg-transparent cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          ×
        </button>
        {title && (
          <DialogTitle as="h2" className="text-lg font-semibold text-gray-900 dark:text-gray-100 pr-8 m-0">
            {title}
          </DialogTitle>
        )}
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto p-4 px-6 text-gray-700 dark:text-gray-300">
        {children}
      </div>

      {/* Footer section */}
      {footer && (
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200/70 dark:border-gray-600/70 flex justify-end gap-2">
          {footer}
        </div>
      )}
    </BaseDialog>
  );
}
